<template>
	<neko-field v-bind="{ ...$props, ...$attrs }" :for="id" v-on="$listeners">
		<div class="input" :class="{ 'is-focused': focused }">
			<div v-if="value.length" class="tags m-0 pl-2 is-flex-wrap-nowrap">
				<span v-for="(tag, index) in value" :key="index" class="tag mb-0">
					{{ tag }}
					<button
						v-if="!disabled"
						class="delete is-small"
						@click.prevent="deleteTag(index)"
						v-tooltip="$t('delete')"
					></button>
				</span>
			</div>

			<input
				v-bind="{ ...$props, ...$attrs }"
				ref="input"
				:id="id"
				v-model="entry"
				@blur="onBlur"
				@focus="onFocus"
				@keydown="onKeyDown"
				@input="onInput"
				:class="{'is-danger': isValid == false, 'is-success': isValid}"
			/>
		</div>

		<template v-if="isValid != null" #icons-left>
			<span v-if="isValid" class="icon has-text-success is-left">
				<span class="fa fa-check"></span>
			</span>
			<span v-else class="icon has-text-danger is-left">
				<span class="fa fa-times"></span>
			</span>
		</template>

		<slot v-for="(slot, name) in $slots" :name="name" :slot="name"></slot>
	</neko-field>
</template>

<script lang="ts">
import { Component, Ref } from "vue-property-decorator";
import { InputComponent } from "@emasofts/common-vuejs-form";

import { Field as NekoField } from "@/components/form";

@Component({
	components: {
		NekoField
	}
})
export default class Tags extends InputComponent {
	declare public readonly disabled: boolean;
	declare public readonly pattern: string;
	declare public readonly sending: boolean;
	declare public readonly value: string[];

	@Ref()
	public readonly input?: HTMLInputElement;

	public entry = "";
	public focused = false;

	public get isValid(): boolean | null {
		if ((this.entry?.length ?? 0) > 0 && this.input?.checkValidity() != null) {
			return this.input.checkValidity();
		}

		return null;
	}

	public addTag(): void {
		if (!this.entry || !this.input?.checkValidity()) {
			this.input?.reportValidity();
		} else if (this.value.every(value => value !== this.entry)) {
			const value = this.value.slice();
			value.push(this.entry);
			this.$emit("input", value);
			this.entry = "";
		}
	}

	public deleteTag(index: number): void {
		if (!this.disabled && !this.sending) {
			const value = this.value.slice();
			value.splice(index, 1);
			this.$emit("input", value);
			this.input?.focus();
		}
	}

	public onBlur(): void {
		this.entry = "";
		this.focused = false;
	}

	public onFocus(event: FocusEvent): void {
		this.focused = true;
		this.$emit("focus", event);
	}

	public onInput(): void {
		this.input?.setCustomValidity("");
	}

	public onKeyDown(event: KeyboardEvent) {
		if (event.key === "Tab") {
			this.addTag();
		} else if (event.key === " " || event.key === "Enter") {
			event.preventDefault();
			this.addTag();
		}
	}
}
</script>

<style lang="sass">
$control-padding-vertical: 0.5em
$control-padding-horizontal: 0.75em

.input
	align-items: center
	display: flex
	overflow-x: auto
	padding: 0

	input
		background: transparent
		border: none
		height: 100%
		min-width: 10em
		width: 100%
		padding: $control-padding-vertical $control-padding-horizontal
</style>

<i18n>
{
	"fr": {
		"delete": "Retirer"
	}
}
</i18n>
